import { IconList, IconReceiptOff, IconSendOff } from "@tabler/icons-react";

import { MissingServicesFilters, MissingServiceStatus } from "@/billing/types/missing-services";
import { getEncounterTypeFilterOptions } from "@/billing/utils/encounter-type-filter";

import {
  MissingServicesColumn,
  MissingServicesFilterSection,
  MissingServicesSummaryCard,
  MissingServiceStatusDisplay,
} from "../types/missing-services";

// The Figma header row is 72px tall. Body rows reach 72px on their own, from
// verticalSpacing="md" plus the two-line client cell.
export const TABLE_HEADER_HEIGHT = 72;

export const REMOVE_ICON_SIZE = 20;

export const LOAD_ERROR_MESSAGE = "Unable to load Missing Services. Please try again.";
export const EMPTY_MESSAGE = "No encounters are missing services for this practice.";
export const REFRESH_MISSING_SERVICES_TABLE_LABEL = "Refresh missing services table";

/** The chip label and color for each missing-Services state. */
export const MISSING_SERVICE_STATUS_DISPLAY: Record<
  MissingServiceStatus,
  MissingServiceStatusDisplay
> = {
  "no-services": { label: "NO SERVICES", color: "red" },
  "services-not-sent": { label: "SERVICES NOT SENT", color: "orange" },
};

export const MISSING_SERVICES_COLUMNS: MissingServicesColumn[] = [
  { key: "client-name", label: "Client Name", width: "16%" },
  { key: "date-of-encounter", label: "Date of Encounter", width: "22%", sortKey: "date" },
  { key: "encounter-type", label: "Encounter Type", width: "19%" },
  { key: "provider", label: "Provider", width: "15%" },
  { key: "service-status", label: "Service Status", width: "18%" },
  { key: "actions", label: "Actions", width: "10%", align: "right" },
];

/** The summary cards, in display order. Each shows a practice-wide count, not a per-page one. */
export const MISSING_SERVICES_SUMMARY_CARDS: MissingServicesSummaryCard[] = [
  {
    id: "no-services",
    title: "No Services",
    description: "Encounter has no services",
    countKey: "noServices",
    icon: IconReceiptOff,
    iconColor: "var(--mantine-color-red-7)",
  },
  {
    id: "services-not-sent",
    title: "Services Not Sent",
    description: "Encounter must be sent to services",
    countKey: "servicesNotSent",
    icon: IconSendOff,
    iconColor: "var(--mantine-color-orange-7)",
  },
  {
    id: "open-items",
    title: "Open Items",
    description: "Total tasks needing attention",
    countKey: "openItems",
    icon: IconList,
    iconColor: "var(--mantine-color-gray-6)",
  },
];

/** Nothing selected in any section. The drawer's Reset button goes back to this. */
export const EMPTY_MISSING_SERVICES_FILTERS: MissingServicesFilters = {
  providerIds: [],
  statuses: [],
  encounterTypeKeys: [],
};

/**
 * The Encounter section's option list, straight from `getEncounterTypeFilterOptions()`. Its keys are
 * the same ValueSet-derived keys `getEncounterTypeFilterTags()` maps to `meta.tag` tokens for the
 * query, so the drawer can never list an option the filter doesn't recognize.
 */
export const MISSING_SERVICES_ENCOUNTER_FILTER_SECTION: MissingServicesFilterSection = {
  id: "encounter",
  title: "Encounter",
  options: getEncounterTypeFilterOptions(),
};

/**
 * The Provider section's copy and test id. Unlike the two checkbox sections it has no static option
 * list — its options are the practice's eligible signers, fetched when the drawer opens — and it is a
 * single multi-select rather than a list, so its test id names the input rather than a section wrapper.
 */
export const MISSING_SERVICES_PROVIDER_FILTER_TESTID =
  "missing-services-filter-provider-multiselect";
export const MISSING_SERVICES_PROVIDER_FILTER_TITLE = "Provider";
export const MISSING_SERVICES_PROVIDER_FILTER_PLACEHOLDER = "Search providers";
export const MISSING_SERVICES_PROVIDER_FILTER_LOADING_PLACEHOLDER = "Loading providers...";

/** The By Status section's option list. Keys match `MissingServiceStatus` values directly. */
export const MISSING_SERVICES_STATUS_FILTER_SECTION: MissingServicesFilterSection = {
  id: "status",
  title: "By Status",
  options: [
    { key: "no-services", label: "No services" },
    { key: "services-not-sent", label: "Services not sent" },
  ],
};
